import { useColorMode } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed } from 'vue';

/** Light, dark, or whatever the system is set to. */
export type ThemePreference = 'light' | 'dark' | 'auto';

/**
 * Where the preference is kept. `index.html` reads it too, before the bundle loads, so the key and
 * the values are a contract with that script — `store.spec.ts` runs it to hold both to it.
 */
export const THEME_STORAGE_KEY = 'back-office.theme';

/**
 * The theme.
 *
 * Kept in this browser, not in the account: the back-office stores nothing about a person but
 * their email. The design system does dark with a `.dark` class on the root and nothing else, so
 * applying it is toggling that class — `useColorMode` does it, and also keeps other tabs in step
 * and freezes transitions for the switch, or every `transition-colors` would fade on its own.
 *
 * Light until someone picks, even on a dark system: every screen was drawn and checked in light,
 * so dark is something a person asks for, not something they find one morning.
 *
 * Instantiated from the root component, so it applies on every screen, the login included.
 */
export const useThemeStore = defineStore('theme', () => {
  const mode = useColorMode({ storageKey: THEME_STORAGE_KEY, initialValue: 'light' });

  const preference = computed<ThemePreference>({
    get: () => mode.store.value,
    set: (value) => {
      mode.store.value = value;
    },
  });

  return { preference };
});
